/* eslint-disable indent */
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { constructArrayObjectAsString, getPageName } from 'utils/commonUtils';
import Lottie from 'lottie-react';
import { LoadingSpinnerAnimation } from 'assets/animations';
import { ArrowRightIcon, CaretDownIcon, EditIcon, NavCloseIcon, SendIcon, WhiteInfoIcon } from 'assets/icons';
import { CustomPopover, GenericDeleteModal, GenericError, If, NoPermissions, SidePanel } from 'components';
import { MoreMenu } from './index';
import {
  InvoiceAdditionalInfoSection,
  InvoiceCustomerSection,
  InvoiceCustomerSignatureSection,
  InvoiceHeaderSection,
  InvoiceLineItemsViewSection,
  InvoicePaymentsSection,
  InvoiceRelatedDocumentsSection,
  InvoiceReminderDetail,
  InvoiceTimelineSection
} from './detail-sections';
import EditReminderModal from 'components/reminder-modal/EditReminderModal';
import { ReminderConfig } from 'components/reminder-modal/types';
import { ReminderDateTypeLabel } from 'containers/settings/constants';
import {
  transformInvoiceApiRemindersToComponentFormat,
  transformInvoiceRemindersToApiFormat
} from '../create-invoice/util';
import { VoidFn } from 'types/baseTypes';
import {
  useLazyDownloadInvoiceAsPDFQuery,
  useLazyDownloadPrintInvoiceAsPDFQuery,
  useLazyGetInvoiceByIdV2Query,
  useSendInvoiceReminderMutation,
  useUpdateInvoiceRemindersMutation
} from '../api';
import {
  getNotifications,
  selectCurrentTeam,
  selectCurrentTeamCustomersPermissions,
  selectCurrentTeamInvoicePermissions,
  selectIsActionsBlocked,
  selectUserDetails,
  showNotifier
} from 'containers/app/appSlice';
import { NotifierTypes } from 'constants/notifierConstants';
import { useAppDispatch, useAppSelector } from 'hooks';
import { SidePanelTypes } from 'constants/sidePanelConstants';
import { toggleIsFromPreview } from '../edit-invoice/invoicesEditSlice';
import { ENTITY_TYPE, PopoverArrowTypes } from 'constants/common';
import { CleverTapEventsInvoices } from '../events';
import { logAnalyticEvent } from 'utils/analytics';
import { InfoMesssages } from 'types/infoMessages';
import { InvoiceHistoryRouteState, SidePanelRouteState } from 'routes/types';
import { DEFAULT_SIDE_PANEL_ROUTE_STATE, FROM_SIDE_PANEL_ROUTE_STATE, ROOT_ROUTE_STATE } from 'routes/constants';
import { updateIsInitialisingJobFromAPI } from 'containers/jobs/create-edit-job/createJobSlice';
import routesPath from 'routes/RoutesPath';
import { useUnlinkJobAndInvoiceMutation } from 'containers/jobs/api';
import { useDisconnectJobOrInvoiceFromEstimateMutation } from 'containers/estimates/api';
import { UnlinkSourceType } from 'containers/estimates/constants';
import MerchantOrTeamBlockedInfoPanel from 'components/merchant-or-team-blocked/MerchantOrTeamBlockedInfoPanel';
import { selectHasNoFSMPlan } from 'containers/subscriptions/modalSlice';
import { InvoicePanelPages, StatusIndicator, TransactionStatus } from '../constants';
import DateUtils from 'utils/dateUtils';
import TransactionsList from './TransactionsList';

interface InvoicePanelProps {
  onClose?: VoidFn;
  routeToNextPage?: ({ type }: { type: SidePanelTypes; id: string }) => void;
}

const InvoicePanel: FC<InvoicePanelProps> = () => {
  const navigate = useNavigate();
  const customerPermissions = useAppSelector(selectCurrentTeamCustomersPermissions);
  const invoicePermissions = useAppSelector(selectCurrentTeamInvoicePermissions);
  const notifications = useAppSelector(getNotifications);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const currentUser = useAppSelector(selectUserDetails);
  const isActionsBlocked = useAppSelector(selectIsActionsBlocked);
  const hasNoFSMPlan = useAppSelector(selectHasNoFSMPlan);

  const {
    rootInvoiceId,
    invoiceId: invoiceIdFromUrl,
    rootReceiptId,
    rootPage,
    rootCustomerId,
    rootDisputeId,
    rootJobId,
    rootEstimateId
  } = useParams();
  const location = useLocation();
  const timezone = DateUtils.getTimezoneLongName();
  const { handleSidePanelClose, refetchEstimatesList, refetchJobDetails } = useOutletContext<any>();

  const [transactionsList, setTransactionsList] = useState([]);
  const [isViewAll, setIsViewAll] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [showBackArrow, setShowBackArrow] = useState(false);
  const [currentPage, setCurrentPage] = useState<InvoicePanelPages>(InvoicePanelPages.DETAILS);
  const [hasPermissionsToView, setHasPermissionsToView] = useState(true);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<{
    id: string;
    isOpen: boolean;
    type: ENTITY_TYPE;
  }>({
    id: '',
    isOpen: false,
    type: null
  });
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);

  const [downloadInvoiceAsPDF] = useLazyDownloadInvoiceAsPDFQuery();
  const [downloadPrintInvoiceAsPDF] = useLazyDownloadPrintInvoiceAsPDFQuery();
  const [updateInvoiceReminders, { isLoading: isUpdatingReminders }] = useUpdateInvoiceRemindersMutation();
  const [sendInvoiceReminder, { isLoading: isSendingReminder }] = useSendInvoiceReminderMutation();

  const [getInvoiceByIdQuery, getInvoiceByIdState] = useLazyGetInvoiceByIdV2Query();
  const {
    data: invoiceData,
    isSuccess: invoiceDataSuccess,
    isFetching: fetchingInvoiceData,
    isUninitialized: isInvoiceDataUninitialized
  } = getInvoiceByIdState;

  const [unlinkJobAndInvoiceMutation, { isSuccess: jobAndInvoiceUnlinkSuccess, isLoading: isUnlinkInProgress }] =
    useUnlinkJobAndInvoiceMutation();
  const [disconnectJobOrInvoiceFromEstimate, { isLoading: isDisconnecting }] =
    useDisconnectJobOrInvoiceFromEstimateMutation();

  useEffect(() => {
    if (jobAndInvoiceUnlinkSuccess) {
      getInvoiceByIdQuery({
        invoiceId: invoiceIdFromUrl ? invoiceIdFromUrl : rootInvoiceId,
        teamId: currentTeam.id,
        timezone
      });
      handleCloseConfirmationModal();
      if (refetchEstimatesList) {
        refetchEstimatesList();
      }
    }
  }, [jobAndInvoiceUnlinkSuccess]);

  useEffect(() => {
    if (invoiceDataSuccess) {
      if (isCreatedBySelf()) {
        if (!invoicePermissions.viewInvoicesByTeam && !invoicePermissions.viewInvoicesBySelf) {
          setHasPermissionsToView(false);
        }
      } else if (!invoicePermissions.viewInvoicesByTeam) {
        setHasPermissionsToView(false);
      }
    }
  }, [invoicePermissions.viewInvoicesByTeam, invoicePermissions.viewInvoicesBySelf, invoiceData]);

  useEffect(() => {
    if (invoiceDataSuccess && invoiceData?.invoice?.lineItems) {
      logAnalyticEvent(CleverTapEventsInvoices.webInvoicesListDetailsPanelOpen, {
        'Line Items': constructArrayObjectAsString(invoiceData.invoice.lineItems, 'Line Item'),
        Page: getPageName(location.pathname)
      });
    }
  }, [invoiceDataSuccess, invoiceData]);

  useEffect(() => {
    if (currentTeam?.id) {
      if (invoiceIdFromUrl) {
        getInvoiceByIdQuery({ invoiceId: invoiceIdFromUrl, teamId: currentTeam.id, timezone });
      } else if (rootInvoiceId) {
        getInvoiceByIdQuery({ invoiceId: rootInvoiceId, teamId: currentTeam.id, timezone });
      }
    }
  }, [invoiceIdFromUrl, rootInvoiceId, currentTeam]);

  useEffect(() => {
    let routeStateTimeout;
    const routeState = location.state as SidePanelRouteState;
    if (routeState?.fromSidePanel) {
      setShowBackArrow(true);
    }
    if (routeState?.shouldAnimate) {
      routeStateTimeout = setTimeout(() => {
        navigate(location.pathname, { replace: true, state: DEFAULT_SIDE_PANEL_ROUTE_STATE });
      }, 800);
    }
    return () => {
      clearTimeout(routeStateTimeout);
    };
  }, [location]);

  const invoice = invoiceData?.invoice;
  const { invoiceCustomer: customer, transactions, invoiceId, isModifiable } = invoice ?? {};
  const txnsTotalCount = invoice?.transactionCount ?? 0;

  const TXNLIMIT = 5;

  const dispatch = useAppDispatch();

  const alterTxnList = (endIndex: number) => {
    if (!transactions || !Array.isArray(transactions)) {
      setTransactionsList([]);
      return;
    }
    const transactionsArray = transactions.slice(0, endIndex);
    setTransactionsList(transactionsArray);
  };

  const isCreatedBySelf = () => {
    return invoice?.merchantInfo?.id === currentUser?.id;
  };

  useEffect(() => {
    if (transactions && transactions.length > 0) {
      const sliceEndIndex = txnsTotalCount > TXNLIMIT ? TXNLIMIT : txnsTotalCount;
      alterTxnList(sliceEndIndex);
    }
  }, [transactions, txnsTotalCount]);

  const isShareEnabled = () => {
    return (
      customerPermissions.viewCustomersCreatedByTeam ||
      customerPermissions.editCustomersCreatedByTeam ||
      customerPermissions.editExistingCustomers
    );
  };

  const handleEditRemindersClick = () => {
    setIsReminderModalOpen(true);
  };

  const handleCloseReminderModal = () => {
    setIsReminderModalOpen(false);
  };

  const handleSaveReminders = async (reminders: ReminderConfig[]) => {
    try {
      const apiReminders = transformInvoiceRemindersToApiFormat(reminders);
      await updateInvoiceReminders({
        teamId: currentTeam.id,
        invoiceId: String(invoiceId),
        reminders: apiReminders
      }).unwrap();

      setIsReminderModalOpen(false);
    } catch (error) {
      // Error handling is managed by RTK Query extraOptions
    }
  };

  const handleSendReminder = () => {
    if (!currentTeam?.id || !invoiceId) {
      return;
    }

    sendInvoiceReminder({
      teamId: currentTeam.id.toString(),
      invoiceId: String(invoiceId)
    });
  };

  const handleInvoiceShare = () => {
    if (invoice.statusIndicator !== StatusIndicator.FULLY_PAID) {
      if (rootInvoiceId) {
        if (invoiceIdFromUrl) {
          navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-pending-invoice/${invoiceId}`, {
            state: FROM_SIDE_PANEL_ROUTE_STATE
          });
        } else {
          navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-pending-invoice`, {
            state: FROM_SIDE_PANEL_ROUTE_STATE
          });
        }
      } else if (rootPage) {
        navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/share-pending-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootReceiptId) {
        navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/share-pending-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootCustomerId) {
        navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/share-pending-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootDisputeId) {
        navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/share-pending-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootJobId) {
        navigate(`/jobs/${currentTeam?.id}/${rootJobId}/share-pending-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootEstimateId) {
        navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/share-pending-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      }
    } else if (rootInvoiceId) {
      if (invoiceIdFromUrl) {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-invoice`, { state: FROM_SIDE_PANEL_ROUTE_STATE });
      }
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/share-invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/share-invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/share-invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/share-invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/share-invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/share-invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  const bottomBar = useMemo(() => {
    if (!invoice) return [];

    const shareHandler = () => {
      logAnalyticEvent(CleverTapEventsInvoices.webInvoicesListDetailReShare, {
        'Invoice Id': invoiceId
      });
      handleInvoiceShare();
    };

    const editHandler = () => {
      logAnalyticEvent(CleverTapEventsInvoices.webInvoicesListDetailEdit);
      dispatch(toggleIsFromPreview(false));
      navigate(`/invoices/edit/${invoiceId}`);
    };

    const reshareHandler = () => {
      logAnalyticEvent(CleverTapEventsInvoices.webInvoicesListDetailReShare);
      handleInvoiceShare();
    };

    return !isModifiable
      ? [
          {
            id: 'invoice-sidepanel-bottom-share-button',
            icon: (
              <div>
                <SendIcon className="h-5" />
              </div>
            ),
            disabledReason: InfoMesssages.shareReceiptNoPermissionMessage,
            btnText: 'Share Invoice',
            isDisabled: !isShareEnabled(),
            onClickHandler: shareHandler
          }
        ]
      : [
          {
            id: 'invoice-sidepanel-bottom-edit-button',
            icon: (
              <div>
                <EditIcon className="h-5" />
              </div>
            ),
            btnText: 'Edit',
            disabledReason: InfoMesssages.editInvoiceNoPermissionMessage,
            isDisabled: !isModifiable,
            onClickHandler: editHandler
          },
          {
            id: 'invoice-sidepanel-bottom-reshare-button',
            isDisabled: !isShareEnabled(),
            btnText: 'Share Invoice',
            disabledReason: InfoMesssages.shareInvoiceNoPermissionMessage,
            onClickHandler: reshareHandler
          }
        ];
  }, [invoice, invoiceId, isModifiable, isShareEnabled, handleInvoiceShare, dispatch, navigate]);

  const moreAnchorElem = () => (
    <div
      id="invoice-sidepanel-bottom-more-button"
      className="flex shrink grow cursor-pointer items-center justify-center gap-2 bg-secondary
    py-3.5 text-sbase font-semibold text-primary hover:opacity-95">
      <div>More</div>
      <div className="path-stroke-current path">
        <CaretDownIcon className="rotate-180" />
      </div>
    </div>
  );

  const toggleIsMoreOpen = useCallback(() => {
    setIsMoreOpen(!isMoreOpen);
  }, [isMoreOpen]);

  const closeMoreOption = useCallback(() => {
    setIsMoreOpen(false);
  }, []);

  const toggleTxnViewAll = () => {
    if (isViewAll) {
      alterTxnList(TXNLIMIT);
      setIsViewAll(false);
      setCurrentPage(InvoicePanelPages.DETAILS);
    } else {
      setCurrentPage(InvoicePanelPages.TRANSACTIONS);
    }
  };

  const handleTransactionsBack = () => {
    setCurrentPage(InvoicePanelPages.DETAILS);
  };

  const onSidePanelClose = useCallback(() => {
    if (showBackArrow) {
      navigate(-1);
    } else {
      handleSidePanelClose();
    }
  }, [showBackArrow, navigate, handleSidePanelClose]);

  const onExportAsPdf = () => {
    dispatch(
      showNotifier({
        message: {
          primaryMessage: 'Downloading started in the background',
          secondaryMessage: ''
        },
        type: NotifierTypes.INFO,
        showClose: false,
        fontStyle: 'text-primary font-normal'
      })
    );
    downloadInvoiceAsPDF({ invoiceId });

    logAnalyticEvent(CleverTapEventsInvoices.webInvoicesListDetailMoreExportPDF, {
      Page: getPageName(location.pathname)
    });
  };

  const onPrintPdf = () => {
    logAnalyticEvent(CleverTapEventsInvoices.webInvoicesListDetailMorePrint, {
      Page: getPageName(location.pathname)
    });
    downloadPrintInvoiceAsPDF({ invoiceId });
  };

  const handleTxnClick = (txnId: string, status = '') => {
    if (invoicePermissions.viewReceiptsByTeam || invoicePermissions.viewReceiptsBySelf) {
      if (status === TransactionStatus.REFUNDED) {
        handleRefundClick(txnId);
      } else {
        if (rootInvoiceId) {
          navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/receipt/${txnId}`, {
            state: FROM_SIDE_PANEL_ROUTE_STATE
          });
        } else if (rootPage) {
          navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/receipt/${txnId}`, {
            state: FROM_SIDE_PANEL_ROUTE_STATE
          });
        } else if (rootReceiptId) {
          navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/receipt/${txnId}`, {
            state: FROM_SIDE_PANEL_ROUTE_STATE
          });
        } else if (rootCustomerId) {
          navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/receipt/${txnId}`, {
            state: FROM_SIDE_PANEL_ROUTE_STATE
          });
        } else if (rootDisputeId) {
          navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/receipt/${txnId}`, {
            state: FROM_SIDE_PANEL_ROUTE_STATE
          });
        } else if (rootJobId) {
          navigate(`/jobs/${currentTeam?.id}/${rootJobId}/receipt/${txnId}`, {
            state: FROM_SIDE_PANEL_ROUTE_STATE
          });
        } else if (rootEstimateId) {
          navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/receipt/${txnId}`, {
            state: FROM_SIDE_PANEL_ROUTE_STATE
          });
        }
      }
    }
  };

  const handleDisputeClick = (dsId: string) => {
    if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/dispute/${dsId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/dispute/${dsId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/dispute/${dsId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/dispute/${dsId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/dispute/${dsId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/dispute/${dsId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/dispute/${dsId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  const getInvoiceName = () => {
    if (customer?.name || customer?.lastName) {
      return `${customer.name || ''} ${customer.lastName || ''}`.trim();
    }
    return invoice?.invoiceNumber || '';
  };

  const onInfoClick = (id, infoMessage) => {
    const existingNotification = notifications.find(notification => notification.id === id);
    if (existingNotification) return;

    dispatch(
      showNotifier({
        id,
        message: {
          primaryMessage: infoMessage,
          secondaryMessage: '',
          isMessageHtml: true
        },
        type: NotifierTypes.WARNING,
        showClose: true,
        fontStyle: 'text-primary font-normal'
      })
    );
  };

  const onInvoiceDetailsClick = () => {
    if (rootInvoiceId) {
      if (invoiceIdFromUrl) {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/invoice-details/${invoiceIdFromUrl}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/invoice-details`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      }
    } else if (rootPage) {
      navigate(
        `/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/invoice-details/
        ${invoiceId}`,
        {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        }
      );
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/invoice-details/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/invoice-details/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/invoice-details/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/invoice-details/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/invoice-details/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  const handleCustomerClick = () => {
    if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/customer/${customer.id}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/customer/${customer.id}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/customer/${customer.id}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/customer`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/customer/${customer.id}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/customer/${customer.id}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/customer/${customer.id}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  const onAddJobFromInvoice = () => {
    if (hasNoFSMPlan) {
      navigate(routesPath.JOBS);
      return;
    }
    if (!customer?.serviceAddress || customer.serviceAddress.length === 0 || !customer?.serviceAddress?.[0]?.id) {
      onInfoClick(invoiceId, InfoMesssages.noServiceAddressMessageForJob);
    } else {
      dispatch(updateIsInitialisingJobFromAPI(true));
      navigate(`${routesPath.CREATE_JOB}?invoiceId=${invoiceId}`);
    }
  };

  const handleRemoveJobFromInvoice = async ({ jobId }: { jobId: string }) => {
    await unlinkJobAndInvoiceMutation({
      jobId,
      invoiceId: invoiceId,
      teamId: currentTeam.id,
      sourceType: UnlinkSourceType.INVOICE
    });
    if (refetchEstimatesList) {
      refetchEstimatesList();
    }
    if (refetchJobDetails) {
      handleSidePanelClose();
      refetchJobDetails();
    }
  };

  const handleOpenRemoveJobFromInvoiceConfirmationModal = (jobId: string) => {
    setIsConfirmationModalOpen({ id: jobId, isOpen: true, type: ENTITY_TYPE.JOB });
  };

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen({ id: '', isOpen: false, type: null });
  };

  const handleJobClick = (jobId: string) => {
    if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam.id}/${rootInvoiceId}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam.id}/${rootJobId}/jobs/${jobId}`, {
        state: ROOT_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam.id}/${rootReceiptId}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam.id}/${rootDisputeId}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam.id}/${rootCustomerId}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  const handleEstimateClick = (estimateId: string) => {
    if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam.id}/${rootInvoiceId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  const handleRefundClick = (txnId: string) => {
    if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/refund-receipt/${txnId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/refund-receipt/${txnId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/refund/${txnId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/refund-receipt/${txnId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/refund-receipt/${txnId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/refund-receipt/${txnId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/refund-receipt/${txnId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  const handleRemoveFromInvoice = () => {
    if (isConfirmationModalOpen.type === ENTITY_TYPE.JOB) {
      handleRemoveJobFromInvoice({ jobId: isConfirmationModalOpen.id });
    } else if (isConfirmationModalOpen.type === ENTITY_TYPE.ESTIMATE) {
      handleRemoveEstimate(isConfirmationModalOpen.id);
    }
  };

  const handleOpenRemoveEstimateConfirmationModal = (estimateId: string) => {
    setIsConfirmationModalOpen({ id: estimateId, isOpen: true, type: ENTITY_TYPE.ESTIMATE });
  };

  const handleRemoveEstimate = async (estimateId: string) => {
    try {
      await disconnectJobOrInvoiceFromEstimate({
        teamId: currentTeam.id,
        estimateId: estimateId,
        entityType: ENTITY_TYPE.INVOICE,
        entityID: invoiceId,
        sourceType: UnlinkSourceType.INVOICE
      }).unwrap();
      handleCloseConfirmationModal();
      if (refetchEstimatesList) {
        refetchEstimatesList();
      }
    } catch (error) {
      // Handle error (e.g., show a notification)
    }
  };

  const handleBackClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleCollectPayment = () => {
    const routeState = {
      ...FROM_SIDE_PANEL_ROUTE_STATE
    };
    if (rootInvoiceId) {
      if (invoiceIdFromUrl) {
        navigate(
          routesPath.INVOICE_INVOICE_BY_ID_COLLECT_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '')
            .replace(':rootInvoiceId', rootInvoiceId)
            .replace(':invoiceId', invoiceId),
          { state: routeState }
        );
      } else {
        navigate(
          routesPath.INVOICE_BY_ID_COLLECT_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '').replace(
            ':rootInvoiceId',
            rootInvoiceId
          ),
          { state: routeState }
        );
      }
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/invoice/${invoiceId}/collect-payment`, {
        state: routeState
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/invoice/${invoiceId}/collect-payment`, {
        state: routeState
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/invoice/${invoiceId}/collect-payment`, {
        state: routeState
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/invoice/${invoiceId}/collect-payment`, {
        state: routeState
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/invoice/${invoiceId}/collect-payment`, {
        state: routeState
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/invoice/${invoiceId}/collect-payment`, {
        state: routeState
      });
    }
  };

  const handleRecordPayment = () => {
    const routeState = { ...FROM_SIDE_PANEL_ROUTE_STATE };
    if (rootInvoiceId) {
      if (invoiceIdFromUrl) {
        navigate(
          routesPath.INVOICE_RECORD_PAYMENT_INVOICE.replace(':teamId', currentTeam?.id.toString() || '')
            .replace(':rootInvoiceId', rootInvoiceId)
            .replace(':invoiceId', invoiceId),
          { state: routeState }
        );
      } else {
        navigate(
          routesPath.INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '').replace(
            ':rootInvoiceId',
            rootInvoiceId
          ),
          { state: routeState }
        );
      }
    } else if (rootJobId) {
      navigate(
        routesPath.JOB_INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '')
          .replace(':rootJobId', rootJobId)
          .replace(':invoiceId', invoiceId),
        { state: routeState }
      );
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/record-payment/${invoiceId}`, {
        state: routeState
      });
    } else if (rootReceiptId) {
      navigate(
        routesPath.RECEIPT_INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '')
          .replace(':rootReceiptId', rootReceiptId)
          .replace(':invoiceId', invoiceId),
        { state: routeState }
      );
    } else if (rootCustomerId) {
      navigate(
        routesPath.CUSTOMER_INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '')
          .replace(':rootCustomerId', rootCustomerId)
          .replace(':invoiceId', invoiceId),
        { state: routeState }
      );
    } else if (rootDisputeId) {
      navigate(
        routesPath.DISPUTE_INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '')
          .replace(':rootDisputeId', rootDisputeId)
          .replace(':invoiceId', invoiceId),
        { state: routeState }
      );
    } else if (rootEstimateId) {
      navigate(
        routesPath.ESTIMATE_INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '')
          .replace(':rootEstimateId', rootEstimateId)
          .replace(':invoiceId', invoiceId),
        { state: routeState }
      );
    }
  };

  const handleViewHistory = () => {
    const routeState: InvoiceHistoryRouteState = {
      ...FROM_SIDE_PANEL_ROUTE_STATE,
      timelineEvents: invoice?.timeline || [],
      invoiceStatusIndicator: invoice?.statusIndicator
    };

    if (rootInvoiceId) {
      if (invoiceIdFromUrl) {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/history/${invoiceId}`, {
          state: routeState
        });
      } else {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/history`, {
          state: routeState
        });
      }
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/history/${invoiceId}`, {
        state: routeState
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/history/${invoiceId}`, {
        state: routeState
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/history/${invoiceId}`, {
        state: routeState
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/history/${invoiceId}`, {
        state: routeState
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/history/${invoiceId}`, {
        state: routeState
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/history/${invoiceId}`, {
        state: routeState
      });
    }
  };

  return (
    <SidePanel isOpen={true} onClose={handleSidePanelClose} shouldAnimate={location?.state?.shouldAnimate}>
      {isActionsBlocked ? (
        <MerchantOrTeamBlockedInfoPanel onBack={handleBackClick} />
      ) : (
        <div className="flex h-full w-[400px] max-w-[400px] flex-col bg-[#F7F7F7]">
          <>
            {fetchingInvoiceData ? (
              <div className="flex h-full w-[400px] items-center justify-center">
                <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
              </div>
            ) : (
              invoiceDataSuccess && (
                <>
                  <If condition={currentPage === InvoicePanelPages.DETAILS}>
                    <div className="flex items-center gap-2 bg-white py-5 px-4">
                      <div onClick={onSidePanelClose} className="cursor-pointer">
                        {showBackArrow ? (
                          <ArrowRightIcon className="rotate-180" id="invoice-panel-back-arrow" />
                        ) : (
                          <NavCloseIcon id="invoice-panel-close" />
                        )}
                      </div>
                      <div id="invoice-panel-head" className="text-17px font-semibold text-headingGray">
                        {invoice?.invoiceNumber || ''} - {getInvoiceName()}
                      </div>
                    </div>
                    {hasPermissionsToView ? (
                      <>
                        <div
                          className="customNormalScroll flex max-w-[400px] grow flex-col 
                            gap-3 overflow-y-auto overflow-x-hidden px-2.5 py-3">
                          <InvoiceHeaderSection
                            invoice={invoice}
                            onJobClick={handleJobClick}
                            onEstimateClick={handleEstimateClick}
                          />

                          <If condition={Boolean(customer?.id)}>
                            <InvoiceCustomerSection customer={customer} onCustomerClick={handleCustomerClick} />
                          </If>

                          <InvoiceTimelineSection
                            status={invoice.status}
                            timelineEvents={invoice.timeline}
                            onViewHistory={handleViewHistory}
                            handleShareInvoice={handleInvoiceShare}
                            handleSendReminder={handleSendReminder}
                            isActionLoading={isSendingReminder}
                          />

                          <InvoicePaymentsSection
                            transactionsList={transactionsList}
                            txnsTotalCount={txnsTotalCount}
                            onTxnClick={handleTxnClick}
                            onDisputeClick={handleDisputeClick}
                            onToggleViewAll={toggleTxnViewAll}
                            onCollectPayment={handleCollectPayment}
                            onRecordPayment={handleRecordPayment}
                            invoiceStatusIndicator={invoice.statusIndicator}
                            onRefundClick={handleRefundClick}
                          />

                          <InvoiceLineItemsViewSection
                            invoice={invoice}
                            onInvoiceDetailsClick={onInvoiceDetailsClick}
                          />

                          <If condition={invoice?.reminderEnabled}>
                            <InvoiceReminderDetail
                              reminders={invoice.reminders}
                              isEditAllowed={true}
                              onEditClick={handleEditRemindersClick}
                            />
                          </If>

                          <InvoiceRelatedDocumentsSection
                            relatedJobs={invoice.relatedJobs}
                            relatedEstimates={invoice.relatedEstimates}
                            onJobClick={handleJobClick}
                            onRemoveJob={handleOpenRemoveJobFromInvoiceConfirmationModal}
                            onAddJobFromInvoice={onAddJobFromInvoice}
                            onEstimateClick={handleEstimateClick}
                            onRemoveEstimate={handleOpenRemoveEstimateConfirmationModal}
                          />

                          <If condition={invoice?.signatureEnabled}>
                            <InvoiceCustomerSignatureSection invoice={invoice} />
                          </If>

                          <InvoiceAdditionalInfoSection invoice={invoice} />
                        </div>
                        <div className="mt-auto flex justify-between gap-px justify-self-end">
                          {bottomBar.map(item => (
                            <button
                              onClick={() => {
                                if (item.isDisabled) {
                                  onInfoClick(item.id, item.disabledReason);
                                } else {
                                  item.onClickHandler();
                                }
                              }}
                              id={`invoice-side-panel-${item.btnText}-button`}
                              key={item.id + item.btnText}
                              className={twMerge(`flex shrink grow cursor-pointer items-center justify-center gap-2
                  bg-secondary py-3.5 font-semibold text-primary hover:opacity-95            
                  ${item.isDisabled ? 'bg-[#9BBADD] hover:opacity-100' : ''}
                  }`)}>
                              <div className={`${item.isDisabled && 'opacity-50'}`}>{item.icon}</div>
                              <div className={`text-sbase ${item.isDisabled && 'opacity-50'}`}>{item.btnText}</div>
                              {item.isDisabled && <WhiteInfoIcon />}
                            </button>
                          ))}
                          <div
                            className={`flex shrink grow cursor-pointer items-center justify-center
                gap-2 bg-secondary ${isMoreOpen ? '' : 'hover:opacity-95'}`}
                            onClick={toggleIsMoreOpen}>
                            <CustomPopover
                              show={isMoreOpen}
                              anchorComponent={moreAnchorElem}
                              onTogglePopover={toggleIsMoreOpen}
                              onClosePopover={closeMoreOption}
                              offset="-right-3 bottom-14"
                              minWidth="min-w-[160px]"
                              showArrow={true}
                              arrowType={PopoverArrowTypes.BOTTOM}
                              highlightAnchor={false}
                              showOnTop={true}>
                              {
                                <MoreMenu
                                  onPrintPdf={onPrintPdf}
                                  onExportPdf={onExportAsPdf}
                                  statusIndicator={invoice.statusIndicator as StatusIndicator}
                                  isModifiable={invoice.isModifiable}
                                  hasRefundableTransaction={invoice.hasRefundableTransaction}
                                />
                              }
                            </CustomPopover>
                          </div>
                        </div>
                      </>
                    ) : (
                      <NoPermissions />
                    )}
                  </If>
                  <If condition={currentPage === InvoicePanelPages.TRANSACTIONS}>
                    <TransactionsList
                      transactions={invoice.transactions || []}
                      invoiceNumber={invoice?.invoiceNumber || ''}
                      onBack={handleTransactionsBack}
                      onTxnClick={handleTxnClick}
                      onDisputeClick={handleDisputeClick}
                      onRefundClick={handleRefundClick}
                    />
                  </If>
                </>
              )
            )}
            {!fetchingInvoiceData && !invoiceDataSuccess && !isInvoiceDataUninitialized && (
              <GenericError handleErrorBack={onSidePanelClose} />
            )}
          </>
        </div>
      )}
      {isConfirmationModalOpen.isOpen && (
        <GenericDeleteModal
          onPrimaryBtnClick={handleRemoveFromInvoice}
          onSecondaryBtnClick={handleCloseConfirmationModal}
          isActionInProgress={isUnlinkInProgress || isDisconnecting}
          primaryBtnId="remove-job/estimate-confirm"
          primaryBtnLabel="Yes, remove"
          secondaryBtnId="remove-job/estimate-cancel"
          secondaryBtnLabel="No, cancel"
          showModal={true}
          title={`Remove ${isConfirmationModalOpen.type === ENTITY_TYPE.JOB ? 'job' : 'estimate'}`}
          subTitle={`Are you sure you want to remove ${
            isConfirmationModalOpen.type === ENTITY_TYPE.JOB ? 'job' : 'estimate'
          } from invoice?`}
        />
      )}
      {isReminderModalOpen && invoice && (
        <EditReminderModal
          isOpen={isReminderModalOpen}
          onClose={handleCloseReminderModal}
          onSave={handleSaveReminders}
          title="Payment reminders"
          subtitle="Reminders will be sent to customer's email"
          referenceDate={invoice?.invoiceDueDate || ''}
          dateType={ReminderDateTypeLabel.DUE_DATE}
          isLoading={isUpdatingReminders}
          initialReminders={transformInvoiceApiRemindersToComponentFormat(invoice?.reminders)}
        />
      )}
    </SidePanel>
  );
};

export default InvoicePanel;
